import { FieldsRenderer } from '@neo/blocks/forms/fields-renderer'
import type { GenericFieldConfig } from '@neo/blocks/forms/types'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@neo/primitives/tabs'
import { EnhancedTextArea } from 'v2/shared/components/enhanced-textarea'
import { NodevarList } from '../components/nodevar-list'
import { v2validations } from '../config/validation.config'
import { useFlowStore } from '../store'
import type { AgentNodeNodeData, SidebarComponentProps } from '../types'
import { AgentTasksSection } from './ai-agents/agent-tasks-section'
import { AiAgentV2Actions, validateAiAgentV2Actions } from './ai-agents/ai-agent-v2-actions'
import { callStartBehaviorField } from './screening-shared'
import { GenericSidebarComponent } from './sidebar.base'

// The agent_node sidebar (SER-5959): the voice-only successor of ai_agent_v2.
// Two tabs, mirroring the AI conversation node: Instructions (with the
// variables-as-tasks sections directly below the instructions) and Actions.
// No channel selector: the type is call-only. Intents are authored on the
// canvas exactly like the AI conversation node.

type FormValues = AgentNodeNodeData['config']

const fields = (nodeVars: string[], isEditing: boolean): GenericFieldConfig<FormValues> => {
  const validations = v2validations({ nodeVars }).agent_node
  return [
    {
      name: 'name',
      label: 'Name',
      zodValidation: validations.shape.name,
      componentType: 'input',
      inputProps: {
        placeholder: 'Enter name',
      },
    },
    {
      name: 'tabs',
      type: 'basic',
      component: ({ form }) => (
        <Tabs defaultValue="instructions" className="col-span-2">
          <TabsList variant="default" className="mb-4 flex">
            <TabsTrigger
              value="instructions"
              data-testid="tab-instructions"
              variant="default"
              className="flex-1 justify-center instructions"
            >
              Instructions
            </TabsTrigger>
            <TabsTrigger
              value="actions"
              data-testid="tab-actions"
              variant="default"
              className="flex-1 justify-center actions"
            >
              Actions
            </TabsTrigger>
          </TabsList>
          <TabsContent
            value="instructions"
            forceMount
            className="data-[state=inactive]:hidden instructions flex flex-col gap-4"
          >
            <FieldsRenderer<FormValues>
              form={form}
              fields={[
                {
                  label: 'Add instructions',
                  name: 'instructions',
                  type: 'custom',
                  component: ({ value, onChange, ...props }) => (
                    <EnhancedTextArea
                      value={value as string}
                      onChange={onChange}
                      className="min-h-[30px]"
                      {...props}
                    />
                  ),
                  eventDataExtractor: (e) => e,
                  zodValidation: validations.shape.instructions,
                  inputProps: {
                    placeholder:
                      'Provide detailed instructions to guide the AI on how to handle the conversation.',
                    rows: 4,
                    endIcon: (
                      <NodevarList
                        nodevars={nodeVars}
                        inputElementSelector="#instructions .ql-ref"
                      />
                    ),
                  },
                },
                {
                  name: 'agent_tasks',
                  type: 'custom',
                  component: ({ form: innerForm }) => (
                    <AgentTasksSection form={innerForm} nodeVars={nodeVars} />
                  ),
                  eventDataExtractor: (e) => e,
                  zodValidation: validations.shape.agent_tasks,
                },
                callStartBehaviorField<FormValues>(validations.shape.initial_wait_time, {
                  collapsible: true,
                }),
              ]}
            />
          </TabsContent>
          <TabsContent value="actions" forceMount className="data-[state=inactive]:hidden actions">
            <AiAgentV2Actions
              form={form as unknown as Parameters<typeof AiAgentV2Actions>[0]['form']}
              nodeVars={nodeVars}
              isEditing={isEditing}
            />
          </TabsContent>
        </Tabs>
      ),
    },
  ]
}

// Same save-time action validation as ai_agent_v2: paint field errors on the
// broken action cards (the actions array is not a registered form field, so
// GenericForm's own zod pass never sees it). Module scope: uses no component
// state (reads the form instance from the store), so it is built once.
const handleFormSave = (data: FormValues) => {
  if (data.actions?.length) {
    const sidebarForm = useFlowStore.getState().sidebarFormInstance as
      | Parameters<typeof validateAiAgentV2Actions>[0]
      | null
    if (sidebarForm) validateAiAgentV2Actions(sidebarForm)
  }
  return data
}

export const AgentNodeSidebarComponent = (props: SidebarComponentProps<AgentNodeNodeData>) => {
  const { nodeVars, selectedNode } = props
  const isEditing = useFlowStore((state) => state.isEditing)
  const formFields = fields(nodeVars, isEditing)

  const defaultValues = {
    ...selectedNode.data.config,
    channel: 'call' as const,
  }

  return (
    <GenericSidebarComponent
      defaultValues={defaultValues}
      fields={formFields}
      callback={handleFormSave}
      {...props}
    />
  )
}
